import { Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { NotificationInboxStore } from '../../../core/store/notification-inbox.store';
import { Notification } from '../../../core/services/notification-inbox.service';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToggleSwitch } from 'primeng/toggleswitch';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [ProgressSpinnerModule, DatePipe, NgTemplateOutlet, FormsModule, ToggleSwitch],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0, transform: 'translateY(-8px)' })),
      ]),
    ]),
  ],
  template: `
    <div class="notif-panel" [@slideIn]>
      <div class="panel-header">
        <span class="panel-title">Notifications</span>
        <div class="panel-actions">
          <button class="action-btn" (click)="store.markAllRead()">Mark all as read</button>
          <button class="close-btn" (click)="store.togglePanel()">
            <i class="pi pi-times"></i>
          </button>
        </div>
      </div>

      <div class="filter-row">
        <span class="filter-label">Unread Only</span>
        <p-toggleswitch [(ngModel)]="unreadOnly" />
      </div>

      @if (store.loading()) {
        <div class="panel-loading">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '32px', height: '32px' }" />
        </div>
      } @else {
        @if (today().length) {
          <ng-container *ngTemplateOutlet="group; context: { label: 'Today', items: today() }" />
        }
        @if (yesterday().length) {
          <ng-container *ngTemplateOutlet="group; context: { label: 'Yesterday', items: yesterday() }" />
        }
        @if (last5Days().length) {
          <ng-container *ngTemplateOutlet="group; context: { label: '5d', items: last5Days() }" />
        }
        @if (!today().length && !yesterday().length && !last5Days().length) {
          <div class="empty-state">No notifications</div>
        }
      }
    </div>

    <ng-template #group let-label="label" let-items="items">
      <div class="group-label">{{ label }}</div>
      @for (n of items; track n.notification_id) {
        <button type="button" class="notif-item" [class.unread]="!n.is_read" (click)="store.navigateTo(n)">
          <div class="notif-header-row">
            <div class="notif-title">{{ n.title }}</div>
            <div class="notif-time-row">
              <span class="notif-time">{{ n.created_at | date:'M/d/yyyy h:mm a' }}</span>
              @if (!n.is_read) {
                <span class="unread-dot"></span>
              }
            </div>
          </div>
          <div class="notif-body">{{ n.body }}</div>
        </button>
      }
    </ng-template>
  `,
  styles: [`
    .notif-panel {
      position: fixed;
      top: 40px;
      right: 41px;
      width: 368px;
      max-height: 480px;
      overflow-y: auto;
      overflow-x: hidden;
      scrollbar-gutter: stable;
      background: var(--p-surface-overlay, #fff);
      border: 1px solid var(--p-surface-border, #e5e7eb);
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
      z-index: 1000;
    }
    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      position: sticky;
      top: 0;
      background: inherit;
      z-index: 1;
    }
    .panel-title { font-weight: 700; font-size: 20px; }
    .panel-actions { display: flex; align-items: center; gap: 8px; }
    .action-btn {
      background: none; border: none; cursor: pointer;
      font-size: 14px; font-weight: 500; line-height: 16px; color: #3367FF;
    }
    .close-btn {
      background: none; border: none; cursor: pointer;
      color: var(--p-text-muted-color, #6b7280); font-size: 14px;
      &:hover { color: var(--p-text-color, #111); }
    }
    .filter-row {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      padding: 8px 16px 4px;
    }
    .filter-label { font-size: 14px; font-weight: 500; color: var(--p-text-color, #111); }
    .panel-loading {
      display: flex; justify-content: center; padding: 32px;
    }
    .group-label {
      padding: 0 20px 4px;
      font-size: 14px; font-weight: 200;
      color: var(--p-text-muted-color, #6b7280);
      text-align: left;
    }
    .notif-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      gap: 4px;
      padding: 8px;
      box-sizing: border-box;
      cursor: pointer;
      width: calc(100% - 16px);
      margin: 0 8px 8px;
      border-radius: 8px;
      transition: background 0.1s;
      border: none; background: transparent; text-align: left; font: inherit;
      &.unread { background: #FFF9E7; }
      &:hover { background: #F5F5F7; }
      &.unread .notif-title { font-weight: 500; }
    }
    .notif-header-row {
      display: flex; align-items: center;
      justify-content: space-between; gap: 8px;
      align-self: stretch;
      min-width: 0;
    }
    .notif-title { font-size: 14px; font-weight: 400; line-height: 1.3; flex: 1; min-width: 0; }
    .notif-body {
      align-self: stretch;
      font-size: 12px; color: var(--p-text-muted-color, #6b7280);
      white-space: pre-line;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }
    .notif-time-row {
      display: flex; align-items: center; gap: 6px;
      flex-shrink: 0;
    }
    .notif-time { font-size: 12px; font-weight: 200; color: var(--p-text-muted-color, #9ca3af); white-space: nowrap; }
    .unread-dot {
      width: 6px; height: 6px;
      aspect-ratio: 1/1;
      border-radius: 50%;
      background: #111;
      flex-shrink: 0;
    }
    .empty-state {
      padding: 32px 16px; text-align: center;
      color: var(--p-text-muted-color, #6b7280); font-size: 13px;
    }
  `],
})
export class NotificationPanelComponent {
  readonly store = inject(NotificationInboxStore);

  unreadOnly = signal(false);

  private readonly filterUnread = (items: Notification[]) =>
    this.unreadOnly() ? items.filter(n => !n.is_read) : items;

  today = computed(() => this.filterUnread(this.store.today()));
  yesterday = computed(() => this.filterUnread(this.store.yesterday()));
  last5Days = computed(() => this.filterUnread(this.store.last5Days()));
}
